// ECharts option assembly. Pure: data + theme in, option out.
//
// Honesty rendering rules enforced here:
// - connectNulls: false on every series — a null epoch is a visible gap.
// - Our line renders wherever delegator_apy_pct is non-null, INCLUDING
//   coverage-gated epochs: the gate applies to cluster statistics only:
//   suppressing our own real data would be a false statement of absence.
// - Median/band are gated together (series.ts); a run of >=3 consecutive
//   covered epochs renders the continuous area + both edge lines, a run of
//   1-2 renders a per-epoch whisker (custom series) instead, so a single
//   covered epoch never re-creates the invisible-sliver-area defect.
// - The y axis auto-scales (scale: true, no min/max) so peaks are never
//   clipped.
// - The in-plot title/subtitle and the framed stats box are gone: the
//   header and answer band are shadow-DOM HTML/SVG built by
//   vf-apy-chart.ts, not ECharts graphics. Percentile standing (rank/
//   percentile facts) lives only in tooltip rows and the band's
//   subdetail lines — no in-plot lane; a per-epoch dot lane was tried
//   and rejected as a low-information, low-legibility encoding.

import type {
  DataZoomComponentOption,
  GridComponentOption,
  LegendComponentOption,
  MarkAreaComponentOption,
  MarkLineComponentOption,
  TooltipComponentOption,
} from "echarts/components";
import type { CustomSeriesOption, LineSeriesOption } from "echarts/charts";
import type { ComposeOption } from "echarts/core";
import type { CustomSeriesRenderItemAPI, CustomSeriesRenderItemParams } from "echarts";

import { formatUtcMonthDay } from "./format";
import { coverageRuns, gatedRegions, BAND_RUN_MIN_LENGTH, type Assembled } from "./series";
import type { ThemeTokens } from "./theme";

export type VfChartOption = ComposeOption<
  | LineSeriesOption
  | CustomSeriesOption
  | GridComponentOption
  | TooltipComponentOption
  | DataZoomComponentOption
  | LegendComponentOption
>;

// One entity, one name: every legend/tooltip-group label for "our
// validator"/"cluster median"/"top decile" shares the same prefix words
// across both bases, differentiated only by a " · <basis>" suffix — no
// second vocabulary for the same series depending on mode.
export const SERIES_OUR = "our validator · net";
export const SERIES_MEDIAN = "cluster median · net";
export const SERIES_BAND = "top decile · net (p90-max)";

// Base (inflation-only) mirror: primary series when Assembled.inflationKeysPresent
// is true (see buildOption's basePrimary branch below).
export const SERIES_OUR_BASE = "our validator · base";
export const SERIES_MEDIAN_BASE = "cluster median · base";
export const SERIES_BAND_BASE = "top decile · base (p90-max)";
/** Secondary net line drawn alongside the base primary, so MEV spikes render honestly as what they are. */
export const SERIES_OUR_NET_DASHED = "our validator · net incl MEV";

export const SANS_FONT = '-apple-system, "Segoe UI", "Helvetica Neue", Arial, sans-serif';
export const MONO_FONT =
  'ui-monospace, "SF Mono", "Cascadia Code", "Roboto Mono", Consolas, monospace';

// Show the zoom slider only when there is history worth browsing.
export const SLIDER_MIN_EPOCHS = 30;

// Layout, top to bottom inside the chart canvas: legend band, plot, x-axis
// label rows, dataZoom slider. Grid gutters (left/right) are axis-fit
// geometry, exempt from the rhythm spacing scale.
const SLIDER_BOTTOM = 8;
const SLIDER_HEIGHT = 24;
const XAXIS_ROWS_HEIGHT = 28;
const GRID_BOTTOM_GAP = 8;
const GRID_BOTTOM_PLAIN = XAXIS_ROWS_HEIGHT + GRID_BOTTOM_GAP;
export const GRID_LEFT = 54;
export const GRID_RIGHT = 16;
const GRID_TOP = 40;

// Narrow-container chart profile. Geometry here is ECharts option values,
// which no stylesheet can reach, so the collapse ladder (vf-apy-chart)
// threads a layout into every option builder; the wide default reproduces
// the settled desktop numbers exactly, keeping desktop renders (and the
// SSR/test paths, which never pass a layout) byte-identical.
export type ChartLayoutTier = "wide" | "compact";
export interface ChartLayout {
  /** "compact" below the host-width rung; grid gutters tighten and the legend anchors left and wraps. */
  tier: ChartLayoutTier;
  /** Coarse primary pointer (touch): the dataZoom slider row grows so its handles reach a draggable size. Orthogonal to tier — a narrow mouse embed stays fine-pointer. */
  coarse: boolean;
}
export const WIDE_LAYOUT: ChartLayout = { tier: "wide", coarse: false };

// Compact-tier legend DISPLAY labels (formatter only — series names, and
// with them legend toggling and tooltip identity, are untouched). The
// tooltip's own row grammar ("ours", "median", "band p90-max") applied
// to the legend: four desk-length labels wrap to three rows at ~360px
// and a wider device sans pushes them to four, overrunning the
// GRID_TOP_COMPACT seat — the short grammar holds two rows down to the
// 320px floor on any font.
const COMPACT_LEGEND_LABEL: Record<string, string> = {
  [SERIES_OUR]: "ours",
  [SERIES_OUR_BASE]: "ours",
  [SERIES_MEDIAN]: "median",
  [SERIES_MEDIAN_BASE]: "median",
  [SERIES_BAND]: "band p90-max",
  [SERIES_BAND_BASE]: "band p90-max",
  [SERIES_OUR_NET_DASHED]: "net incl MEV",
};
// 46: the widest y label ("5.8%", 11px mono) plus its margin still clears;
// 64: seats the left-anchored legend at its short compact grammar
// ("ours / median / band p90-max / net incl MEV") — one row above
// ~380px, two rows down to the 320px floor even on a wide device sans;
// two rows must never overlap the plot.
const GRID_LEFT_COMPACT = 46;
const GRID_RIGHT_COMPACT = 12;
const GRID_TOP_COMPACT = 64;
const SLIDER_HEIGHT_COARSE = 32;

// Dense x-axis mode (>=15 epochs): a date sub-label appears only every
// DENSE_DATE_INTERVAL ticks, so the axis does not collide with itself.
const DENSE_DATE_EPOCH_THRESHOLD = 15;
const DENSE_DATE_INTERVAL = 14;

const WHISKER_WIDTH = 11;
const MEDIAN_TICK_HEIGHT = 2;

// z-ladder (field design, operator direction 2026-07-13): the top-decile
// range reads as a FIELD — the band area is the reference ground and the
// hero is a thin crisp thread through it — never as two lines competing
// for the same pixels (edge-above-hero produced symbol/AA artifacts at
// every coincident epoch, which on live data is nearly all of them).
// Ground vs figure, WEIGHT-REVERSED (operator direction: the top-decile
// range is the FAT ranged line, our validator the THIN thread inside
// it). On live data the range spans ~0.1pp of a ~2pp axis — a few
// pixels — so fill alpha alone can never make it read; the 3px edges
// fuse with the fill into one fat green stroke at that height, and the
// 1.5px hero thread rides visibly inside/on it. Both series at true
// coordinates, nothing offset or recolored.
// HERO_LINE_WIDTH/Z_HERO are exported: the income view (income-view.ts)
// renders its single line with the same hero treatment.
export const HERO_LINE_WIDTH = 1.5;
const Z_BAND_FILL = 1;
const Z_GAP_FILL = 2;
const Z_MEDIAN = 3;
const Z_NET = 4;
const Z_BAND_OUTLINE = 5;
export const Z_HERO = 6;
// Bonus native-legend-hover affordance (never the sole findability channel):
// raises the band's area alpha only, never coordinates or stroke width.
const BAND_EMPHASIS_FILL_OPACITY = 0.3;
/** Cluster-line terminus dot: readable as a deliberate stop, not a data point. */
const TERMINUS_SYMBOL_SIZE = 5;

/** Live dataZoom window in slider percent (0-100), carried across re-renders and view switches. */
export interface ZoomWindow {
  start: number;
  end: number;
}

export interface ChartInputs {
  a: Assembled;
  theme: ThemeTokens;
  /** Epoch of the currently pinned card, or null; renders the amber axis tick. */
  pinnedEpoch: number | null;
  /** /income start_time for an epoch, for the x-axis secondary date row. */
  dateForEpoch: (epoch: number) => number | null;
  /** ECharts axis-trigger tooltip formatter (closes over per-epoch data). */
  tooltipFormatter: (params: unknown) => string;
  /** Shadow-DOM container the tooltip must render into (never document.body). */
  tooltipAppendTo: HTMLElement;
  /** User's live legend selection (from 'legendselectchanged'), fed back so re-renders don't reset a revealed line. Absent/empty = defaults apply. */
  legendSelected?: Record<string, boolean>;
  /** User's live dataZoom window, fed back so re-renders and view switches don't reset the slider. Absent = full range. */
  zoom?: ZoomWindow;
  /** Container-width tier + pointer class from the collapse ladder; absent = the settled wide/fine desktop geometry. */
  layout?: ChartLayout;
}

function lineSeries(
  name: string,
  data: readonly (number | null)[],
  color: string,
  width: number,
  z: number,
  lineType: "solid" | "dashed" | "dotted" = "solid",
): LineSeriesOption {
  return {
    name,
    type: "line",
    data: [...data],
    connectNulls: false,
    showSymbol: true,
    symbol: "circle",
    symbolSize: 0,
    lineStyle: { color, width, type: lineType, opacity: 1 },
    itemStyle: { color },
    emphasis: { focus: "none" },
    z,
  };
}

interface OurPoint {
  value: number;
  symbolSize: number;
  itemStyle: { opacity: number };
}

/**
 * Our-line data with the isolated-point symbol rule: a real value with no
 * adjacent rendered segment (both neighbors null or out of range) always
 * shows a 5px dot so it never renders as nothing; the headline epoch gets a
 * permanent 6px dot; every other real point carries a 4px symbol at
 * opacity 0 that the series' emphasis style reveals on hover.
 * Exported: the income view's hero line shares the rule (headline null).
 */
export function ourSeriesData(
  our: readonly (number | null)[],
  headlineIndex: number | null,
): (OurPoint | null)[] {
  return our.map((v, i) => {
    if (v === null) {
      return null;
    }
    const prevConnected = i > 0 && our[i - 1] !== null;
    const nextConnected = i < our.length - 1 && our[i + 1] !== null;
    const isolated = !prevConnected && !nextConnected;
    const isHeadline = i === headlineIndex;
    const size = isHeadline ? 6 : isolated ? 5 : 4;
    const opacity = isHeadline || isolated ? 1 : 0;
    return { value: v, symbolSize: size, itemStyle: { opacity } };
  });
}

/** Coverage-gated epochs: 8% muted wash plus a 45-degree hatch (theme-tuned opacity). */
function gatedMarkArea(
  epochs: readonly number[],
  gated: readonly boolean[],
  t: ThemeTokens,
): MarkAreaComponentOption | undefined {
  const regions = gatedRegions(epochs, gated);
  if (regions.length === 0) {
    return undefined;
  }
  return {
    silent: true,
    itemStyle: {
      color: t.gatedWash,
      decal: {
        symbol: "line",
        dashArrayX: [1, 0],
        dashArrayY: [2, 4],
        rotation: Math.PI / 4,
        color: t.gatedHatch,
        backgroundColor: "transparent",
      },
    } as MarkAreaComponentOption["itemStyle"],
    label: { show: false },
    data: regions.map((r) => [{ xAxis: String(r.startEpoch) }, { xAxis: String(r.endEpoch) }]),
  };
}

/** Amber vertical tick at the pinned epoch; exported for the income view's clicked-epoch mark. */
export function pinnedMarkLine(pinnedEpoch: number | null, t: ThemeTokens): MarkLineComponentOption | undefined {
  if (pinnedEpoch === null) {
    return undefined;
  }
  return {
    silent: true,
    symbol: "none",
    label: { show: false },
    lineStyle: { color: t.amber, width: 2, type: "solid" },
    data: [{ xAxis: String(pinnedEpoch) }],
  };
}

/**
 * First epoch of the Alpenglow era (SIMD-0326): consensus switched from
 * TowerBFT at slot 459,653,000 in epoch 1064, 2026-10-21. Vote credits, the
 * input of every cluster series (median, band, percentile, rank), end with
 * epoch 1063; the API serves no cluster row from 1064 on.
 */
export const ALPENGLOW_EPOCH = 1064;
export const ALPENGLOW_LABEL = "ALPENGLOW ACTIVATION";

/**
 * Vertical event line at the Alpenglow activation, labeled. Drawn on the
 * epoch-1064 category once the spine reaches it; while the spine ends at
 * 1063 (the last vote-credit epoch) it sits on that last category, the
 * plot's right edge, where the series it marks the end of actually ends.
 * Absent for a spine that ends before 1063: a window that predates the
 * switch has nothing to mark.
 */
export function alpenglowMarkLine(
  epochs: readonly number[],
  t: ThemeTokens,
): MarkLineComponentOption | undefined {
  const last = epochs[epochs.length - 1];
  if (last === undefined || last < ALPENGLOW_EPOCH - 1) {
    return undefined;
  }
  const at = epochs.includes(ALPENGLOW_EPOCH) ? ALPENGLOW_EPOCH : last;
  return {
    silent: true,
    symbol: "none",
    label: {
      show: true,
      formatter: ALPENGLOW_LABEL,
      position: "insideEndTop",
      rotate: 90,
      fontSize: 10,
      fontFamily: MONO_FONT,
      color: t.textMuted,
    },
    lineStyle: { color: t.textMuted, width: 1, type: "dashed" },
    data: [{ xAxis: String(at) }],
  };
}

/**
 * One markLine per series is all ECharts allows; this folds several into
 * one, pushing each line's style and label down onto its own data items so
 * nothing is lost in the fold. Undefined inputs are skipped.
 */
export function mergeMarkLines(
  ...lines: (MarkLineComponentOption | undefined)[]
): MarkLineComponentOption | undefined {
  const present = lines.filter((l): l is MarkLineComponentOption => l !== undefined);
  if (present.length === 0) {
    return undefined;
  }
  if (present.length === 1) {
    return present[0];
  }
  type Item = Record<string, unknown> & { lineStyle?: object; label?: object };
  const data = present.flatMap((line) =>
    ((line.data ?? []) as Item[]).map((item) => ({
      ...item,
      lineStyle: { ...(line.lineStyle ?? {}), ...(item.lineStyle ?? {}) },
      label: { ...(line.label ?? {}), ...(item.label ?? {}) },
    })),
  );
  return {
    silent: true,
    symbol: "none",
    label: { show: false },
    data: data as MarkLineComponentOption["data"],
  };
}

interface CoverageClassification {
  bandP90: (number | null)[];
  bandMax: (number | null)[];
  bandDelta: (number | null)[];
  lineMedian: (number | null)[];
  whiskers: { index: number; p90: number; max: number; median: number }[];
}

/**
 * Splits one basis's coverage-gated median/p90/max series into the two
 * band_sparse marks by maximal run length: >=3 consecutive covered epochs
 * render the continuous area + median line; 1-2 render a per-epoch whisker
 * (custom series) + median tick instead of an area sliver or a line into
 * the void.
 */
function classifyCoverage(
  median: readonly (number | null)[],
  p90: readonly (number | null)[],
  max: readonly (number | null)[],
  bandDelta: readonly (number | null)[],
): CoverageClassification {
  // Segment on BAND coverage (p90 AND max present), not median presence: a
  // p50-only or inverted-band epoch must not inflate a run and push isolated
  // band edges through the area path as invisible slivers.
  const bandCovered = p90.map((v, i) => v !== null && max[i] !== null);
  const runs = coverageRuns(bandCovered);
  const bandP90: (number | null)[] = p90.map(() => null);
  const bandMax: (number | null)[] = max.map(() => null);
  const bandDeltaOut: (number | null)[] = bandDelta.map(() => null);
  const lineMedian: (number | null)[] = median.map(() => null);
  const whiskers: { index: number; p90: number; max: number; median: number }[] = [];
  const whiskerIndex = new Set<number>();

  for (const run of runs) {
    if (run.length >= BAND_RUN_MIN_LENGTH) {
      for (let i = run.startIndex; i <= run.endIndex; i += 1) {
        bandP90[i] = p90[i] ?? null;
        bandMax[i] = max[i] ?? null;
        bandDeltaOut[i] = bandDelta[i] ?? null;
      }
      continue;
    }
    for (let i = run.startIndex; i <= run.endIndex; i += 1) {
      const p90v = p90[i];
      const maxv = max[i];
      const medianv = median[i];
      if (
        p90v !== null &&
        p90v !== undefined &&
        maxv !== null &&
        maxv !== undefined &&
        medianv !== null &&
        medianv !== undefined
      ) {
        whiskers.push({ index: i, p90: p90v, max: maxv, median: medianv });
        whiskerIndex.add(i);
      }
    }
  }
  // Median line renders wherever the gate passed the median, except at whisker
  // epochs whose median tick already shows it (no double-draw, no dropped stat).
  for (let i = 0; i < median.length; i += 1) {
    if (median[i] !== null && !whiskerIndex.has(i)) {
      lineMedian[i] = median[i] ?? null;
    }
  }
  return { bandP90, bandMax, bandDelta: bandDeltaOut, lineMedian, whiskers };
}

// Fat translucent band edges (bandEdge ink, 7px at 0.4 opacity) on both
// p90 and max, under the hero: the operator's ranged-line design — the
// boundary reads as a soft wide RANGE, and the opaque 1.5px hero thread
// stays crisp inside it (an opaque fat stroke would swallow the thread).
// No point symbols (they produced the artifact mess); short covered
// runs are whisker-rendered, so an edge segment always spans >= 3
// epochs.
function outlineSeries(name: string, data: readonly (number | null)[], t: ThemeTokens): LineSeriesOption {
  return {
    name,
    type: "line",
    data: [...data],
    connectNulls: false,
    showSymbol: false,
    silent: true,
    lineStyle: { color: t.bandEdge, width: 7, opacity: 0.4 },
    itemStyle: { color: t.bandEdge },
    emphasis: { disabled: true },
    z: Z_BAND_OUTLINE,
  };
}

function bandAreaSeries(name: string, c: CoverageClassification, t: ThemeTokens): LineSeriesOption[] {
  const common: Partial<LineSeriesOption> = {
    type: "line",
    connectNulls: false,
    showSymbol: false,
    silent: true,
    stack: "decile-band",
    emphasis: { disabled: true },
    z: Z_BAND_FILL,
  };
  return [
    {
      ...common,
      name,
      type: "line",
      data: [...c.bandP90],
      lineStyle: { width: 0, opacity: 0 },
      itemStyle: { color: t.band },
    },
    {
      ...common,
      name,
      type: "line",
      data: [...c.bandDelta],
      lineStyle: { width: 0, opacity: 0 },
      itemStyle: { color: t.band },
      areaStyle: { color: t.band, opacity: t.bandFillOpacity },
      // Native legend-hover emphasis: a bonus affordance, never the sole
      // findability channel (that is the recolored fill + p10 edge + area
      // swatch + tooltip). Raises fill alpha only — never coordinates, never
      // stroke width.
      emphasis: { areaStyle: { opacity: BAND_EMPHASIS_FILL_OPACITY } },
    },
    outlineSeries(name, c.bandP90, t),
    outlineSeries(name, c.bandMax, t),
  ];
}

/**
 * Terminus dot: one mark at a cluster line's last measured epoch, drawn only
 * when unmeasured epochs follow it.
 *
 * An epoch's distribution is computed during the following epoch, so our line
 * permanently runs past the cluster's. Unterminated, a cluster line simply
 * stops in open space and reads as a broken chart ("where is this line?").
 * The dot states that the line ends HERE, deliberately; the tooltip on the
 * pending epochs carries the reason. EVERY cluster line that stops gets one —
 * median and both band edges — or a wide band would leave its edges ending
 * naked beside an explained median, and the caption's "cluster lines" would
 * be a claim about marks that are not there. One terminus per line, never
 * per-point symbols: those are the knot artifact.
 */
function pendingTerminusSeries(
  name: string,
  line: readonly (number | null)[],
  pending: readonly boolean[],
  color: string,
  z: number,
): LineSeriesOption | null {
  let last = -1;
  for (let i = 0; i < line.length; i += 1) {
    if (line[i] !== null) {
      last = i;
    }
  }
  const value = last < 0 ? null : line[last];
  if (value === null || value === undefined) {
    return null;
  }
  // Only terminate a line that actually has unmeasured epochs after it: at the
  // right edge of a fully measured range there is nothing to explain.
  if (!pending.slice(last + 1).some(Boolean)) {
    return null;
  }
  const data: (number | null)[] = line.map(() => null);
  data[last] = value;
  return {
    // Shares the owning line's name so ECharts' legend dedupes it away — no
    // entry is added for a mark that is not its own series (bandAreaSeries
    // precedent).
    name,
    type: "line",
    data,
    showSymbol: true,
    symbol: "circle",
    symbolSize: TERMINUS_SYMBOL_SIZE,
    itemStyle: { color },
    lineStyle: { width: 0, opacity: 0 },
    silent: true,
    emphasis: { disabled: true },
    z,
  };
}

export const GAP_FILL_NAME = "our/median gap (base)";

/**
 * Quiet charcoal wash between the our-base and median-base lines, base mode
 * only, epochs where BOTH are non-null. This is not new data — it is an
 * emphasis path over the same two series' difference, already legible from
 * the our and median lines themselves: no outline, no tooltip contribution,
 * no legend entry (see buildOption). Stacked invisible-base + visible-delta
 * pair, same technique as bandAreaSeries; connectNulls:false so a gap epoch
 * on either line breaks the fill rather than bridging it.
 */
function gapFillSeries(
  our: readonly (number | null)[],
  median: readonly (number | null)[],
  t: ThemeTokens,
): LineSeriesOption[] {
  const lower = our.map((v, i) => (v !== null && median[i] !== null ? Math.min(v, median[i] as number) : null));
  const delta = our.map((v, i) => (v !== null && median[i] !== null ? Math.abs(v - (median[i] as number)) : null));
  const common: Partial<LineSeriesOption> = {
    type: "line",
    connectNulls: false,
    showSymbol: false,
    silent: true,
    stack: "gap-fill",
    emphasis: { disabled: true },
    z: Z_GAP_FILL,
  };
  return [
    {
      ...common,
      name: GAP_FILL_NAME,
      type: "line",
      data: [...lower],
      lineStyle: { width: 0, opacity: 0 },
      itemStyle: { color: t.ourLine },
    },
    {
      ...common,
      name: GAP_FILL_NAME,
      type: "line",
      data: [...delta],
      lineStyle: { width: 0, opacity: 0 },
      itemStyle: { color: t.ourLine },
      areaStyle: { color: t.ourLine, opacity: t.gapFillOpacity },
    },
  ];
}

/**
 * Per-epoch whisker (p90-max gold bar, 0.35 alpha, hairline outline) plus an
 * 11x2px blue median tick — the band_sparse rotated-90-degree segment
 * grammar for maximal covered runs shorter than BAND_RUN_MIN_LENGTH.
 */
function whiskerSeries(
  name: string,
  whiskers: readonly { index: number; p90: number; max: number; median: number }[],
  t: ThemeTokens,
): CustomSeriesOption {
  return {
    name,
    type: "custom",
    silent: true,
    z: Z_BAND_OUTLINE,
    renderItem: (_params: CustomSeriesRenderItemParams, api: CustomSeriesRenderItemAPI) => {
      const coordXY = (x: number, y: number): [number, number] => {
        const p = api.coord([x, y]);
        return [p[0] ?? 0, p[1] ?? 0];
      };
      const idx = (api.value(0) as number | undefined) ?? 0;
      const p90 = (api.value(1) as number | undefined) ?? 0;
      const maxV = (api.value(2) as number | undefined) ?? 0;
      const median = (api.value(3) as number | undefined) ?? 0;
      const top = coordXY(idx, maxV);
      const bottom = coordXY(idx, p90);
      const barTop = Math.min(top[1], bottom[1]);
      const barHeight = Math.max(Math.abs(bottom[1] - top[1]), 1);
      const barX = top[0] - WHISKER_WIDTH / 2;
      const medPt = coordXY(idx, median);
      return {
        type: "group",
        children: [
          {
            type: "rect",
            shape: { x: barX, y: barTop, width: WHISKER_WIDTH, height: barHeight },
            style: {
              fill: t.band,
              opacity: t.goldMarkAlpha,
              stroke: t.boundaryOutline,
              lineWidth: 1,
            },
          },
          {
            type: "rect",
            shape: {
              x: top[0] - WHISKER_WIDTH / 2,
              y: medPt[1] - MEDIAN_TICK_HEIGHT / 2,
              width: WHISKER_WIDTH,
              height: MEDIAN_TICK_HEIGHT,
            },
            style: { fill: t.medianLine },
          },
        ],
      };
    },
    data: whiskers.map((w) => [w.index, w.p90, w.max, w.median]),
    encode: { x: 0, y: [1, 2] },
  };
}

function xAxisLabelFormatter(
  a: Assembled,
  t: ThemeTokens,
  dateForEpoch: (epoch: number) => number | null,
): (value: string, index: number) => string {
  const sparse = a.epochs.length < DENSE_DATE_EPOCH_THRESHOLD;
  return (value, index) => {
    const show = sparse || index % DENSE_DATE_INTERVAL === 0;
    if (!show) {
      return `{epoch|${value}}`;
    }
    const dateStr = formatUtcMonthDay(dateForEpoch(Number(value)));
    return `{epoch|${value}}\n{date|${dateStr}}`;
  };
}

// --- shared chart chrome --------------------------------------------------
//
// The category x axis, grid geometry, dataZoom slider, and tooltip config
// are shared by every view (base here, position/mev in
// view-options.ts) so the three plots keep an identical epoch domain,
// slider position, and frame — a view switch changes the series, never the
// coordinate system.

/**
 * Category x axis over the trimmed epoch spine, with the epoch+date rich
 * labels. `boundaryGap` stays false for line views (plot spans edge to
 * edge); bar views pass true (a bar centered on the axis edge would
 * half-clip).
 */
export function fullChartXAxis(
  a: Assembled,
  t: ThemeTokens,
  dateForEpoch: (epoch: number) => number | null,
  boundaryGap = false,
): VfChartOption["xAxis"] {
  return {
    type: "category",
    boundaryGap,
    data: a.epochs.map(String),
    axisLine: { lineStyle: { color: t.axisLine } },
    axisTick: { alignWithLabel: true },
    axisLabel: {
      fontSize: 11,
      fontFamily: MONO_FONT,
      lineHeight: 14,
      interval: "auto",
      hideOverlap: true,
      formatter: xAxisLabelFormatter(a, t, dateForEpoch) as never,
      rich: {
        epoch: { fontSize: 11, fontFamily: MONO_FONT, color: t.axisText, lineHeight: 14 },
        date: { fontSize: 12, fontFamily: MONO_FONT, color: t.textMuted, lineHeight: 14 },
      },
    },
    splitLine: { show: true, lineStyle: { color: t.splitLine } },
  };
}

/**
 * Grid geometry; `showSlider` reserves the dataZoom row (whose height
 * follows the layout's pointer class). `hasTopLegend` gates the compact
 * top: only a view whose legend wraps at the plot's top needs the tall
 * seat — the legend-less views (position, mev, single-series income)
 * keep the plain top at every tier, else the seat renders as dead air.
 */
export function fullChartGrid(
  showSlider: boolean,
  layout: ChartLayout = WIDE_LAYOUT,
  hasTopLegend = true,
): VfChartOption["grid"] {
  const compact = layout.tier === "compact";
  const sliderHeight = layout.coarse ? SLIDER_HEIGHT_COARSE : SLIDER_HEIGHT;
  return {
    left: compact ? GRID_LEFT_COMPACT : GRID_LEFT,
    right: compact ? GRID_RIGHT_COMPACT : GRID_RIGHT,
    top: compact && hasTopLegend ? GRID_TOP_COMPACT : GRID_TOP,
    bottom: showSlider
      ? XAXIS_ROWS_HEIGHT + GRID_BOTTOM_GAP + sliderHeight + SLIDER_BOTTOM
      : GRID_BOTTOM_PLAIN,
    show: false,
  };
}

/**
 * The default slider window: opens the plot on the last `window` epochs
 * (position-window) rather than the full history (181 epochs), so recent
 * epochs are on screen without dragging the slider.
 *
 * This is a viewport default only. The answer band's own window is day-anchored
 * (BAND_TRAILING_DAYS) and self-labeled, independent of this — dragging the
 * slider never moves any band statistic.
 *
 * Exact fractional percents, never rounded — flooring or ceiling the edge
 * drops or duplicates a boundary epoch (the income view shipped a one-epoch
 * hole exactly that way).
 */
export function defaultZoomWindow(epochCount: number, window: number): ZoomWindow {
  if (epochCount <= 0 || window <= 0 || window >= epochCount) {
    return { start: 0, end: 100 };
  }
  return { start: ((epochCount - window) / epochCount) * 100, end: 100 };
}

/**
 * The dataZoom slider, or undefined at or below SLIDER_MIN_EPOCHS. `zoom`
 * restores the user's live window so re-renders and view switches never
 * reset the slider.
 */
export function fullChartSlider(
  a: Assembled,
  t: ThemeTokens,
  zoom?: ZoomWindow,
  layout: ChartLayout = WIDE_LAYOUT,
): VfChartOption["dataZoom"] {
  if (a.epochs.length <= SLIDER_MIN_EPOCHS) {
    return undefined;
  }
  return [
    {
      type: "slider",
      xAxisIndex: 0,
      start: zoom?.start ?? 0,
      end: zoom?.end ?? 100,
      bottom: SLIDER_BOTTOM,
      // Coarse pointer: the taller row is the touch affordance — the
      // handles scale with slider height (handleSize default "100%"),
      // so height alone brings them to a draggable size.
      height: layout.coarse ? SLIDER_HEIGHT_COARSE : SLIDER_HEIGHT,
      borderColor: t.splitLine,
      // Handle: a quiet rounded capsule replaces the stock notched pill
      // — the last default-styled mark on the chart (operator
      // 2026-07-17). Path is a centered round-cornered bar; ECharts
      // scales it to handleSize (75% of the slider row, so it grows
      // with the coarse-pointer row automatically). Same theme tokens,
      // no new ink.
      handleIcon:
        "path://M-3,-10 L3,-10 Q5,-10 5,-8 L5,8 Q5,10 3,10 L-3,10 Q-5,10 -5,8 L-5,-8 Q-5,-10 -3,-10 Z",
      handleSize: "75%",
      // The move-handle grip bar rides the top edge; muted so the
      // selected range reads by its filler, not by chrome.
      moveHandleSize: 6,
      moveHandleStyle: { color: t.textMuted, opacity: 0.35 },
      emphasis: { moveHandleStyle: { color: t.textMuted, opacity: 0.6 } },
      // Restyled off ECharts defaults: quiet neutral track/filler (no
      // default blue), charcoal handles, the data-shadow thumbnail kept
      // but dimmed — it is the only full-history preview at 181 epochs.
      // sliderTrack/sliderFiller are chrome aliases (theme.ts), NOT the
      // gated-region tokens: "your selected range" and "statistics
      // withheld" are unrelated meanings that must re-ink independently.
      backgroundColor: t.sliderTrack,
      fillerColor: t.sliderFiller,
      handleStyle: { color: t.ourLine, borderColor: t.ourLine },
      dataBackground: {
        lineStyle: { color: t.textMuted, opacity: 0.5 },
        areaStyle: { color: t.textMuted, opacity: 0.15 },
      },
      textStyle: { color: t.textMuted, fontSize: 10 },
    },
  ];
}

/** Axis-trigger tooltip config, rendered into the shadow-DOM wrap (never document.body). */
export function fullChartTooltip(
  t: ThemeTokens,
  formatter: (params: unknown) => string,
  appendTo: HTMLElement,
): VfChartOption["tooltip"] {
  return {
    trigger: "axis",
    confine: true,
    appendTo,
    // Width cap relative to the chart wrap (the tooltip's offset parent):
    // confine keeps the layer's POSITION inside the widget but not its
    // WIDTH — the reason-sentence rows (~650px at 12px) sized the layer
    // past a phone container and clipped mid-value. Container-relative
    // only, no absolute cap: on desktop the content never reaches the
    // limit, so the settled desktop tooltip is untouched. extraCssText
    // lands after ECharts' inline white-space:nowrap and overrides it.
    extraCssText: "max-width:calc(100% - 16px);white-space:normal;",
    backgroundColor: t.tooltipBg,
    borderColor: t.tooltipBorder,
    textStyle: { color: t.text, fontSize: 12 },
    axisPointer: { type: "line", snap: true },
    formatter: formatter as never,
  };
}

export function buildOption(i: ChartInputs): VfChartOption {
  const { a, theme: t } = i;

  // Primary series basis: base (inflation-only) once the API serves it,
  // net (incl. MEV) unchanged otherwise — see CLAUDE.md's mode rule. Net
  // p50/p90/max never draw as chart series in base mode (tooltip-only); our
  // own net line still plots, violet and dotted, as a secondary honesty
  // check against MEV lottery spikes.
  const basePrimary = a.inflationKeysPresent === true;
  const primaryOur = basePrimary ? a.ourBase : a.our;
  const primaryMedian = basePrimary ? a.medianBase : a.median;
  const primaryP90 = basePrimary ? a.p90Base : a.p90;
  const primaryMax = basePrimary ? a.maxBase : a.max;
  const primaryBandDelta = basePrimary ? a.bandDeltaBase : a.bandDelta;
  const primaryGated = basePrimary ? a.distributionInflGated : a.distributionGated;
  const primaryHasMedian = basePrimary ? a.hasMedianBase : a.hasMedian;
  const primaryHasBand = basePrimary ? a.hasBandBase : a.hasBand;
  const primaryOurName = basePrimary ? SERIES_OUR_BASE : SERIES_OUR;
  const primaryMedianName = basePrimary ? SERIES_MEDIAN_BASE : SERIES_MEDIAN;
  const primaryBandName = basePrimary ? SERIES_BAND_BASE : SERIES_BAND;

  const covered = primaryMedian.map((v) => v !== null);
  const headlineIndex = (() => {
    for (let idx = a.epochs.length - 1; idx >= 0; idx -= 1) {
      if (covered[idx] === true && primaryOur[idx] !== null) {
        return idx;
      }
    }
    return null;
  })();

  const classification = classifyCoverage(primaryMedian, primaryP90, primaryMax, primaryBandDelta);
  const series: (LineSeriesOption | CustomSeriesOption)[] = [];
  // icon omitted -> ECharts derives the legend swatch from the matching
  // series' own style (true line width/dash for hero/median/net); the band
  // is the one explicit override, an area swatch matching its fill so the
  // legend stops promising a line the chart never shows.
  const legendData: { name: string; icon?: string }[] = [{ name: primaryOurName }];

  // Gap fill: base mode only, silent, no legend entry — the story as area
  // (see gapFillSeries doc comment).
  if (basePrimary) {
    series.push(...gapFillSeries(primaryOur, primaryMedian, t));
  }

  if (primaryHasBand) {
    series.push(...bandAreaSeries(primaryBandName, classification, t));
    legendData.push({ name: primaryBandName, icon: "roundRect" });
  }
  if (classification.whiskers.length > 0) {
    series.push(whiskerSeries(primaryBandName, classification.whiskers, t));
    if (!primaryHasBand) {
      legendData.push({ name: primaryBandName, icon: "roundRect" });
    }
  }
  if (primaryHasMedian) {
    series.push(lineSeries(primaryMedianName, classification.lineMedian, t.medianLine, 2, Z_MEDIAN));
    legendData.splice(1, 0, { name: primaryMedianName });
  }

  // Terminate every cluster line that stops short of our own: the median and
  // both band edges gate together, so they end at the same epoch and must be
  // marked together. Appended after the band/median pushes so the dots sit on
  // top of the ink they terminate.
  const primaryPending = basePrimary ? a.distributionInflPending : a.distributionPending;
  const termini = [
    pendingTerminusSeries(primaryMedianName, classification.lineMedian, primaryPending, t.medianLine, Z_MEDIAN + 1),
    pendingTerminusSeries(primaryBandName, classification.bandP90, primaryPending, t.bandEdge, Z_BAND_OUTLINE + 1),
    pendingTerminusSeries(primaryBandName, classification.bandMax, primaryPending, t.bandEdge, Z_BAND_OUTLINE + 1),
  ];
  for (const terminus of termini) {
    if (terminus !== null) {
      series.push(terminus);
    }
  }

  // Hero weight: the primary our-line (base once served, net otherwise)
  // is the chart's single hero series — a crisp thread, topmost, reading
  // INSIDE the band ribbon. Beneath it, a SURFACE-RING spacer (dataviz
  // mark spec: overlapping marks get a surface-color gap): a silent
  // panel-colored line 1px wider per side, z just below the hero, cutting
  // a thin slit in the translucent ribbon so the neutral thread reads
  // crisply inside it instead of blending. This is NOT the old deleted
  // casing defect: that erased the band's only 1px locators; the ribbon
  // is now a 7px + fill AREA whose findability survives a 3.5px slit —
  // the ribbon stays visible above and below the thread everywhere.
  const ringSeries = lineSeries(`${primaryOurName} (casing)`, primaryOur, t.panelBg, HERO_LINE_WIDTH + 2, Z_HERO - 0.5);
  ringSeries.silent = true;
  ringSeries.tooltip = { show: false };
  const ourSeries = lineSeries(primaryOurName, primaryOur, t.ourLine, HERO_LINE_WIDTH, Z_HERO);
  ourSeries.data = ourSeriesData(primaryOur, headlineIndex) as LineSeriesOption["data"];
  ourSeries.markArea = gatedMarkArea(a.epochs, primaryGated, t);
  ourSeries.markLine = mergeMarkLines(pinnedMarkLine(i.pinnedEpoch, t), alpenglowMarkLine(a.epochs, t));
  ourSeries.emphasis = { itemStyle: { opacity: 1 }, scale: true };
  series.push(ringSeries, ourSeries);

  // Secondary net (incl. MEV) line: only in base mode, and only when there
  // is real net data to show — violet (not charcoal, never the hero hue),
  // 1px solid full opacity (weight, not texture, carries the demotion; a
  // dotted/dashed pattern cannot guarantee slot coverage on a near-vertical
  // MEV spike at responsive widths). Off by default (legend.selected below)
  // — a quiet affordance, not a rival hero at first paint; the operator
  // clicks the legend to reveal it.
  const hasNetDashed = basePrimary && a.our.some((v) => v !== null);
  if (hasNetDashed) {
    series.push(lineSeries(SERIES_OUR_NET_DASHED, a.our, t.netLine, 1, Z_NET, "solid"));
    legendData.push({ name: SERIES_OUR_NET_DASHED });
  }

  const layout = i.layout ?? WIDE_LAYOUT;
  const slider = fullChartSlider(a, t, i.zoom, layout);

  const option: VfChartOption = {
    animation: false,
    textStyle: { fontFamily: SANS_FONT },
    legend: {
      top: 8,
      // Compact: anchor left with both edges set so the four items wrap
      // inside the container (reading direction starts the rows at the
      // plot's left edge), and display the short label grammar; wide
      // keeps the settled right-anchored row with full names.
      ...(layout.tier === "compact"
        ? {
            left: 4,
            right: 4,
            formatter: (name: string): string => COMPACT_LEGEND_LABEL[name] ?? name,
          }
        : { right: GRID_RIGHT }),
      orient: "horizontal",
      itemWidth: 14,
      itemHeight: 3,
      selectedMode: true,
      selected: hasNetDashed ? { [SERIES_OUR_NET_DASHED]: false, ...(i.legendSelected ?? {}) } : undefined,
      tooltip: {
        show: true,
        formatter: (params: unknown): string =>
          (params as { name?: string } | undefined)?.name === SERIES_OUR_NET_DASHED
            ? "cluster band is base-only; this net (incl MEV) figure is not directly comparable"
            : ((params as { name?: string } | undefined)?.name ?? ""),
      },
      data: legendData,
      textStyle: { fontSize: 12, fontWeight: 400, color: t.text, fontFamily: SANS_FONT },
      backgroundColor: "transparent",
      borderWidth: 0,
    },
    grid: fullChartGrid(slider !== undefined, layout),
    xAxis: fullChartXAxis(a, t, i.dateForEpoch),
    yAxis: {
      type: "value",
      axisLabel: { fontSize: 11, fontFamily: MONO_FONT, color: t.axisText, formatter: "{value}%" },
      axisLine: { show: true, lineStyle: { color: t.axisLine } },
      splitLine: { show: true, lineStyle: { color: t.splitLine } },
      // Auto-scale to the data; no hardcoded cap, peaks are never clipped.
      scale: true,
    },
    tooltip: fullChartTooltip(t, i.tooltipFormatter, i.tooltipAppendTo),
    series,
  };

  if (slider !== undefined) {
    option.dataZoom = slider;
  }
  return option;
}
